import { optionService } from "../option";
import { CommandWithFollowup, LayoutsState, ModelStepData, Step, StepService, VariantResource } from "../../types";
import { WorkflowManager } from "../../WorkflowManager";
import { OptionNotFoundError } from "../../util/exception";
import { AssetNotFoundError } from "../../util/exception";

class ModelStepService implements StepService<ModelStepData> {
    async init(
        stepData: Step<ModelStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        const option = stepData.option;
        if (!option) throw new OptionNotFoundError(stepData);
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        } else {
            const defaultVariant = optionService.getDefaultVariant(option, stepData.overrideDefaultVariantId);
            if (defaultVariant) {
                return await this.selectVariantLambda(stepData, defaultVariant, workflowManager, () => {});
            }
        }
        return null;
    }

    private async reload(stepData: Step<ModelStepData>, workflowManager: WorkflowManager, reducerState: LayoutsState) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const option = stepData.option;
        if (serializedStep?.selectedVariants) {
            const variantId = serializedStep.selectedVariants[0].id;
            if (option && variantId) {
                const variant = option.variants?.find((variant: VariantResource) => variant.id === variantId);
                if (variant) {
                    const regionElements = elementsForStep.map((el) => ({
                        id: el.id,
                        region: el.stepRegion,
                        regionIndex: el.stepRegionIndex,
                    }));
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        [variant],
                        regionElements,
                        async () => {
                            const modelContainer = workflowManager.getModelContainer();
                            if (modelContainer && stepData.option) {
                                const highlightedRef = variant.asset?.fileLink;
                                if (!highlightedRef) throw new AssetNotFoundError(variant);
                                await modelContainer.applyModelVariant(
                                    stepData.stepName || "",
                                    {
                                        model: highlightedRef,
                                        contextService: workflowManager.getLayoutPreviewService(),
                                    },
                                    stepData.data.replaceProductModel || false,
                                );
                                workflowManager.setMandatoryFulfilled(stepData.stepName, true);
                            }
                        },
                    );
                }
            }
        }
    }

    async selectVariant(
        step: Step<ModelStepData>,
        variant: VariantResource,
        workflowManager: WorkflowManager,
        setApplying: (status: boolean) => void,
    ) {
        const result = await this.selectVariantLambda(step, variant, workflowManager, setApplying);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    private async selectVariantLambda(
        step: Step<ModelStepData>,
        variant: VariantResource,
        workflowManager: WorkflowManager,
        setApplying: (status: boolean) => void,
    ): Promise<CommandWithFollowup | null> {
        setApplying(true);
        const modelRef = variant.asset?.fileLink;
        if (!modelRef) {
            throw new AssetNotFoundError(variant);
        }
        return {
            command: undefined,
            followup: async () => {
                await workflowManager.setSelectionsAndElements(step.stepName, [variant], [], async () => {
                    try {
                        const modelContainer = workflowManager.getModelContainer();
                        // We let selection logic apply. But if the preview service has failed
                        // to load we don't want to throw an error. Instead we just don't
                        // load the 3D model.
                        if (modelContainer) {
                            await modelContainer.applyModelVariant(
                                step.stepName,
                                {
                                    model: modelRef,
                                    contextService: workflowManager.getLayoutPreviewService(),
                                },
                                step.data.replaceProductModel || false,
                            );
                        }
                        workflowManager.setMandatoryFulfilled(step.stepName, true);
                    } finally {
                        setApplying(false);
                    }
                });
            },
        };
    }
}

export const modelStepService = new ModelStepService();
